import React from 'react';
import { Platform } from '../../types/Platforms';
import { useAppSettingsStore } from '../../stores/appSettingsStore';
import {
  getPlatformLogo,
  getPlatformLogoTreatment,
  getSupplierLogoTileWrapperClasses,
  getPlatformLogoDimensions,
  getMediumPlatformLogoDimensions,
  getLargePlatformLogoDimensions,
  getHugePlatformLogoDimensions,
} from '../../services/helperService';
import { cn } from '../../lib/utils';

export type SupplierLogoSize = 'small' | 'medium' | 'large' | 'huge';

const DIMENSIONS_BY_SIZE: Record<SupplierLogoSize, (platform: Platform, unknownSupplierName?: string) => string> = {
  small: getPlatformLogoDimensions,
  medium: getMediumPlatformLogoDimensions,
  large: getLargePlatformLogoDimensions,
  huge: getHugePlatformLogoDimensions,
};

export interface SupplierLogoProps {
  platform: Platform;
  unknownSupplierName?: string;
  size?: SupplierLogoSize;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
  title?: string;
  draggable?: boolean;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

/**
 * Single render point for supplier logos: resolves the dark-mode image path
 * (raw / stripped-on-dark-canvas / tile-wrapped) via helperService.ts so
 * every logo render site stays in sync with the classifier's treatment map.
 */
export const SupplierLogo: React.FC<SupplierLogoProps> = ({ platform, unknownSupplierName, size = 'medium', className, style, alt, title, draggable, onError }) => {
  const darkMode = useAppSettingsStore(s => s.darkMode);
  const dimensions = DIMENSIONS_BY_SIZE[size](platform, unknownSupplierName);
  const treatment = getPlatformLogoTreatment(platform, unknownSupplierName);
  const src = getPlatformLogo(platform, unknownSupplierName, darkMode);
  const altText = alt ?? unknownSupplierName ?? platform;
  const isTiled = darkMode && treatment === 'tile';

  if (isTiled) {
    return (
      <div className={cn(dimensions, 'rounded-xl', getSupplierLogoTileWrapperClasses(), className)} style={style} title={title}>
        <img src={src} alt={altText} className="w-2/3 h-2/3 object-contain" draggable={draggable} onError={onError} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={altText}
      title={title}
      draggable={draggable}
      className={cn(dimensions, 'object-contain shrink-0', className)}
      style={style}
      onError={onError}
    />
  );
};

export default SupplierLogo;
